<template>
  <div>
    <b-row>
      <b-col
        align="right"
      >
        <b-button
          variant="outline-primary"
          class="mb-1"
          :disabled="loading"
          @click="fetchItems"
        >
          Refresh
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1 ml-1"
          :disabled="deactivating || selectedItemDiscount.length < 1"
          @click="deactivateHandler"
        >
          Deactivate
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1 ml-1"
          @click="showCompanyDiscountList = true"
        >
          Company Discount List
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="showItemDicountForm = true"
        >
          Add Item Discount
        </b-button>
      </b-col>
    </b-row>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <!-- Horizontal scroll wrapper -->
    <div
      v-if="!loading && !loadingError"
      class="table-scroll-wrapper"
    >
      <b-table
        :items="results"
        :fields="tableFields"
        responsive="sm"
        striped
        hover
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        class="compact-table"
        no-local-sorting
        @sort-changed="sortingChanged"
      >
        <template #head(select)>
          <b-form-checkbox
            v-model="isAllItemDiscountSelected"
            :disabled="results.length === 0"
            @change="handleAllItemDiscountSelectChange"
          />
        </template>
        <template #cell(select)="value">
          <b-form-checkbox
            v-model="selectedItemDiscount"
            :value="value.item.id"
          />
        </template>
        <template #cell(batch)="data">
          {{ data.item.linked_products && data.item.linked_products.map(product => product.batch).join(', ') }}
        </template>
        <template #cell(start_date)="data">
          {{ data.item.start_date && formatedDate(data.item.start_date) }}
        </template>
        <template #cell(end_date)="data">
          {{ data.item.end_date && formatedDate(data.item.end_date) }}
        </template>
        <template #cell(status)="{item}">
          <app-chip :variant="getStausChipVariant(item.is_active)">
            {{ item.is_active ? "Active" : "Inactive" }}
          </app-chip>
        </template>
        <template #cell(action)="data">
          <feather-icon
            v-b-tooltip.hover="{boundary:'window'}"
            title="Edit Discount"
            icon="Edit2Icon"
            size="20"
            class="cursor-pointer mr-1"
            @click="editItemDiscount = data.item"
          />
        </template>
      </b-table>
    </div>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />

    <!-- Add / Edit modal (reused) -->
    <add-item-discount
      v-if="showItemDicountForm || editItemDiscount"
      :edit-data="editItemDiscount"
      @modal-closed="closeDiscountModal"
      @item-discount-added="addItemDiscountHandler"
      @item-discount-updated="updateItemDiscountHandler"
    />

    <!-- Company Discount List Modal -->
    <company-discount-list-modal
      v-if="showCompanyDiscountList"
      @modal-closed="showCompanyDiscountList = false"
    />
  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip,
  BFormCheckbox,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import AppChip from '@/components/UI/AppChip.vue'
import DetailedPagination from '../../UI/DetailedPagination.vue'
import AddItemDiscount from './AddItemDisount.vue'
import CompanyDiscountListModal from './CompanyDiscountListModal.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    AppChip,
    DetailedPagination,
    AddItemDiscount,
    CompanyDiscountListModal,
    BFormCheckbox,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      results: [],
      tableFields: [
        { key: 'select' },
        { key: 'product_name', label: 'Item', sortable: true },
        { key: 'batch', label: 'Batches', sortable: true },
        { key: 'start_date', label: 'Start Date', sortable: true },
        { key: 'end_date', label: 'End Date', sortable: true },
        { key: 'discount', label: 'Discount(%)', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'action', label: 'Action' },
      ],
      editItemDiscount: null,
      isAllItemDiscountSelected: false,
      selectedItemDiscount: [],
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      deactivating: false,
      sortBy: 'generation_date',
      sortDesc: true,
      showItemDicountForm: false,
      showCompanyDiscountList: false,
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      const params = {
        sort_by: this.sortBy,
        sort_desc: this.sortDesc,
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }
      this.loading = true
      axios.get(`/marg/product-discounts?${getParsedQueryString(params)}`)
        .then(res => {
          this.results = res.data.results
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || error?.response?.data?.detail || 'Something went wrong'
          this.loading = false
        })
    },

    async deactivateHandler() {
      this.deactivating = true
      try {
        await axios.patch('/marg/product_discount_toggle_active/', { ids: this.selectedItemDiscount, is_active: false })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Status Updated',
            text: `Total ${this.selectedItemDiscount.length} item discount status deactivated successfully`,
            variant: 'success',
          },
        })
        this.deactivating = false
        this.selectedItemDiscount = []
        this.isAllItemDiscountSelected = false
        this.fetchItems()
      } catch (error) {
        this.deactivating = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Status Update Failed',
            text: error?.response?.data?.error || error?.response?.data?.detail || 'Something went wrong',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },

    closeDiscountModal() {
      this.showItemDicountForm = false
      this.editItemDiscount = null
    },

    addItemDiscountHandler() {
      this.showItemDicountForm = false
      this.editItemDiscount = null
      this.currentPage = 1
      this.fetchItems()
    },

    updateItemDiscountHandler() {
      this.editItemDiscount = null
      this.fetchItems()
    },

    getStausChipVariant(value) {
      if (value === true) {
        return 'success'
      }
      return 'danger'
    },

    handleAllItemDiscountSelectChange(isChecked) {
      this.selectedItemDiscount = isChecked
        ? this.results.map(data => data.id)
        : []
    },
  },
}
</script>

<style scoped>
.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper .table {
  min-width: 1200px;
  margin-bottom: 0;
}

.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
